import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Typography, Box, Alert, Chip, Stack,
} from "@mui/material";

/**
 * Per-row edit dialog for the AI config table. The form field type is
 * driven by the registry metadata (`item.type`) so a percent renders
 * as a 0..1 number with a "× 100% =" helper, an integer as a whole
 * number, and a string as a text input.
 *
 * Client-side validation duplicates the registry's hint string for
 * fast feedback, but the server is the source of truth — it
 * re-validates on PUT and returns 400 with the validator hint on
 * mismatch.
 */
export default function ConfigEditDialog({ item, onClose, onSubmit }) {
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (item) {
      setDraft(item.currentValue == null ? "" : String(item.currentValue));
      setLocalError(null);
    }
  }, [item]);

  if (!item) return null;

  const validate = (value) => {
    if (item.type === "percent" || item.type === "integer") {
      const n = parseFloat(value);
      if (Number.isNaN(n)) return "Must be a number";
      if (item.type === "integer" && !Number.isInteger(n)) {
        return "Must be a whole number";
      }
      if (item.min != null && n <= item.min && item.type === "percent") {
        return `Must be greater than ${item.min}`;
      }
      if (item.min != null && n < item.min && item.type === "integer") {
        return `Must be at least ${item.min}`;
      }
      if (item.max != null && n >= item.max && item.type === "percent") {
        return `Must be less than ${item.max}`;
      }
      if (item.max != null && n > item.max && item.type === "integer") {
        return `Must be at most ${item.max}`;
      }
      return null;
    }
    if (item.type === "string") {
      if (!String(value).trim()) return "Cannot be empty";
      return null;
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate(draft);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    setSubmitting(true);
    let value = draft;
    if (item.type === "percent" || item.type === "integer") {
      value = parseFloat(draft);
    }
    await onSubmit(item.key, value);
    setSubmitting(false);
  };

  // Helper text under the field, customised per type.
  let helper = item.validatorHint || "";
  if (item.type === "percent") {
    const n = parseFloat(draft);
    if (!Number.isNaN(n)) {
      helper = `${(n * 100).toFixed(1)}% — ${item.validatorHint}`;
    }
  }

  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          borderLeft: "5px solid",
          borderImage: "linear-gradient(180deg, #007C84, #016268) 1",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #E0F4F5, #F0FAFA)",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#0F172A" }}>
            {item.label}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#007C84", fontWeight: 700 }}>
            {item.key}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ mb: 2, color: "#475569", lineHeight: 1.5 }}>
          {item.description}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`type · ${item.type}`}
            sx={{ fontWeight: 700, bgcolor: "#E0F4F5", color: "#007C84" }}
          />
          <Chip
            size="small"
            label={`default · ${item.defaultValue}`}
            variant="outlined"
            sx={{ fontWeight: 700, fontFamily: "monospace" }}
          />
          {item.category && (
            <Chip
              size="small"
              label={item.category}
              sx={{ fontWeight: 700, bgcolor: "#F1F5F9", color: "#475569" }}
            />
          )}
        </Stack>

        {item.type === "string" ? (
          <TextField
            fullWidth
            label="Value"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            helperText={helper}
            error={!!localError}
          />
        ) : (
          <TextField
            fullWidth
            label="Value"
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={{
              step: item.type === "percent" ? 0.01 : 1,
              min: item.min ?? undefined,
              max: item.max ?? undefined,
            }}
            helperText={helper}
            error={!!localError}
          />
        )}

        {localError && (
          <Alert severity="warning" sx={{ mt: 2 }}>{localError}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            background: "linear-gradient(135deg, #007C84, #016268)",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            boxShadow: "0 4px 12px rgba(0,124,132,0.35)",
            "&:hover": { background: "linear-gradient(135deg, #016268, #014f54)" },
            "&.Mui-disabled": { background: "#E5E7EB", color: "#94A3B8" },
          }}
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
